import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { GraduationCap, Plus } from "lucide-react";
import * as React from "react";

import {
  acknowledgeDealExpiry,
  discardDealDraft,
  dismissDraftCard,
  dismissOutcomeCard,
  fetchDealDashboard,
  listExpiringDeals,
  listMyDeals,
  listMyDrafts,
  stopMeetingRewardReminder,
  type Deal,
  type DealDashboardMetrics,
  type DealListItem,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { DealDashboardHeader } from "@/components/deals/DealDashboardHeader";
import { DealDashboardMetricsSection } from "@/components/deals/DealDashboardMetrics";
import { DealDetailDrawer } from "@/components/deals/DealDetailDrawer";
import {
  DealFeedSidebar,
  deriveExpiredContextCards,
  deriveOutcomeCards,
} from "@/components/deals/DealFeedSidebar";
import { type PartnerDealPresetSort } from "@/components/deals/DealsDataGrid";
import { DealWorkspace } from "@/components/deals/DealWorkspace";
import {
  currentYearClosedFilter,
  EMPTY_DEAL_FILTERS,
  type DealAdvancedFilterState,
} from "@/components/deals/dealFilters";
import { MeetingRewardClaimModal } from "@/components/deals/MeetingRewardClaimModal";
import { isMeetingRewardReminderDue } from "@/components/deals/meetingRewardReminder";
import { DEAL_TAB_STATUSES, type DealTab } from "@/components/deals/dealTabs";
import { Button } from "@/components/ui/button";
import { dealToasts, useToast } from "@/components/ui/toast";
import { isExpiringWithin } from "@/lib/dealExpiry";
import { useTrainingLockout } from "@/lib/useTrainingLockout";

export function PartnerDealsListRoute() {
  const { accessToken, data: me } = useCurrentUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Shared with the workspace search input (mounted in Slice D4); the
  // header's "Search deals" focuses it.
  const searchRef = React.useRef<HTMLInputElement | null>(null);
  // Deep-link search params: ?reviewDealId opens the expiry overlay (below);
  // ?tab pre-selects a workspace tab. CHP-300 Fix 3: after a successful
  // registration the confirmation screen returns here with ?tab=pending so the
  // just-submitted (pending_review) deal is immediately visible "in review".
  const search = useSearch({ strict: false }) as {
    reviewDealId?: string;
    tab?: DealTab;
  };
  // CHP-300: guard the ?tab param — only honour a value that's a known tab,
  // else fall back to the default. An invalid ?tab=garbage would otherwise be
  // cast straight through and later throw in dealMatchesTab. Use hasOwnProperty
  // (not `in`) so prototype keys like ?tab=toString don't slip past the guard.
  const initialTab: DealTab =
    search.tab && Object.prototype.hasOwnProperty.call(DEAL_TAB_STATUSES, search.tab)
      ? search.tab
      : "active";
  // Workspace filter state. Scorecard clicks select a tab; the search box
  // value lives here too so a scorecard click can clear an active search.
  const [activeTab, setActiveTab] = React.useState<DealTab>(initialTab);
  const [searchValue, setSearchValue] = React.useState("");
  // CHP-370: filter state is lifted here (alongside tab + search) so a
  // scorecard click can drive it — "Closed YTD" applies a current-year range.
  const [filters, setFilters] = React.useState<DealAdvancedFilterState>(EMPTY_DEAL_FILTERS);
  // CHP-1038: sort preset from the "My expiring deals" scorecard — the ledger
  // lands soonest-expiry-first. Any other scorecard click restores the default
  // order (each card is a clean preset view, per CHP-370).
  const [sortPreset, setSortPreset] = React.useState<PartnerDealPresetSort | null>(null);

  // CHP-473: a summary-card click applies the matching tab/filter (below) AND
  // scrolls the ledger into view, so the result is visible without a manual
  // scroll past the feed. Same smooth-scroll idiom as the header's search jump.
  const ledgerRef = React.useRef<HTMLDivElement>(null);

  function selectTab(tab: DealTab, opts?: { closedThisYear?: boolean; expiringSoonest?: boolean }) {
    setActiveTab(tab);
    setSearchValue("");
    // CHP-370: the "Total Sales Closed YTD" card lands on the Closed-won tab
    // with a current-year close-date range; every other scorecard is a clean
    // preset view, so it clears any active advanced filter.
    setFilters(opts?.closedThisYear ? currentYearClosedFilter(new Date()) : EMPTY_DEAL_FILTERS);
    // CHP-1038: the expiring-deals card lands soonest-expiry-first.
    setSortPreset(opts?.expiringSoonest ? { column: "expiry", direction: "asc" } : null);
    // CHP-473: bring the ledger into view after applying the preset.
    ledgerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // CHP-1038: a direct tab change drops any scorecard sort preset — left in
  // place it would re-seed on the next grid remount (e.g. switching to an
  // archive tab, which doesn't even render the Expiry column, giving an
  // invisible sort). Manual header sorts are unaffected (grid-internal state).
  function handleTabChange(tab: DealTab) {
    setActiveTab(tab);
    setSortPreset(null);
  }

  // Deal detail opens in a slide-out drawer (the standalone /deals/$dealId
  // route is kept for direct links).
  const [selectedDealId, setSelectedDealId] = React.useState<string | null>(null);

  function openDeal(dealId: string) {
    setSelectedDealId(dealId);
  }

  function closeDrawer() {
    setSelectedDealId(null);
  }

  // RACCPE-22: BDMs with outstanding compulsory training can't register deals.
  const lockout = useTrainingLockout();

  const deals = useQuery<DealListItem[]>({
    queryKey: ["deals", "me", accessToken],
    queryFn: () => listMyDeals(accessToken!),
    enabled: !!accessToken,
    // CHP-310: passive-watcher backstop — a partner staring at the ledger sees
    // server-side changes (approval, expiry/SLA) within 30s without a window
    // refocus. Acting-user mutations already invalidate ["deals"] immediately.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  // Shares the scorecard's cache entry (same queryKey) — feeds the
  // workspace tab bracket counts without a second request.
  const dashboard = useQuery<DealDashboardMetrics>({
    queryKey: ["deals", "dashboard", accessToken],
    queryFn: () => fetchDealDashboard(accessToken!),
    enabled: !!accessToken,
    refetchInterval: 30_000, // CHP-310: keep scorecards in step with the ledger
    refetchIntervalInBackground: false,
  });

  const drafts = useQuery<Deal[]>({
    queryKey: ["deals", "drafts", "me", accessToken],
    queryFn: () => listMyDrafts(accessToken!),
    enabled: !!accessToken,
  });

  const expiring = useQuery<Deal[]>({
    queryKey: ["deals", "expiring", accessToken],
    queryFn: () => listExpiringDeals(accessToken!),
    enabled: !!accessToken,
  });

  const discard = useMutation({
    mutationFn: (dealId: string) => discardDealDraft(accessToken!, dealId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals", "drafts"] });
      void queryClient.invalidateQueries({ queryKey: ["deals", "me"] });
    },
  });

  // Feed-card [X] dismissals (Wave 2). Both non-destructive: the expiring
  // [X] reuses the acknowledge-expiry endpoint (same as the overlay's
  // "leave as is" path) without opening the overlay; the draft [X] stamps
  // draft_dismissed_at so the card hides while the draft survives.
  const dismissExpiring = useMutation({
    mutationFn: (dealId: string) => acknowledgeDealExpiry(accessToken!, dealId),
    // CHP-326/CHP-361: the expiring-card [X] is a real "let it expire" outcome,
    // so it toasts like the overlay's "leave as is" path rather than dismissing
    // silently.
    onSuccess: () => {
      toast(dealToasts.letExpire());
      void queryClient.invalidateQueries({ queryKey: ["deals", "expiring"] });
      void queryClient.invalidateQueries({ queryKey: ["deals", "me"] });
    },
    onError: () => {
      toast(dealToasts.failed("set the deal to expire"));
    },
  });

  const dismissDraft = useMutation({
    mutationFn: (dealId: string) => dismissDraftCard(accessToken!, dealId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals", "drafts"] });
    },
  });

  // Outcome-card [X] — stamps outcome_dismissed_at; the card derives from
  // the "me" list query, so invalidating it drops the card on refetch.
  const dismissOutcome = useMutation({
    mutationFn: (dealId: string) => dismissOutcomeCard(accessToken!, dealId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals", "me"] });
    },
  });

  // Deep-link: ?reviewDealId=<id> opens the expiry decision overlay on
  // mount — the partner main dashboard's "Review Options" lands here (same
  // ?param pattern as the wizard's ?draftId resume). `search` is read above.
  const [reviewingDealId, setReviewingDealId] = React.useState<string | null>(
    search.reviewDealId ?? null,
  );
  const reviewingDeal = (expiring.data ?? []).find((d) => d.id === reviewingDealId) ?? null;
  // Dismissed drafts stay resumable (and deletable) via the wizard — they
  // just stop occupying a feed card.
  const activeDrafts = (drafts.data ?? []).filter(
    (d) => d.deal_status === "draft" && d.draft_dismissed_at === null,
  );

  // Outcome alerts derive from the existing partner list query — recently
  // decided deals (approved/denied within 14 days, not yet dismissed).
  const outcomeDeals = React.useMemo(() => deriveOutcomeCards(deals.data ?? []), [deals.data]);

  // CHP-380: auto-expired deals awaiting a close reason ("DEAL EXPIRED" cards).
  const expiredContextDeals = React.useMemo(
    () => deriveExpiredContextCards(deals.data ?? []),
    [deals.data],
  );

  // CHP-464 (finishing CHP-387): direct-payout first-meeting-reward midpoint
  // cards. The payout gate is per-viewer (a pooled firm's nudge lives on the
  // PO Main Dashboard instead); the deal-level question is the shared
  // predicate's.
  const isDirectPayout = me?.spif_payout_type === "direct";
  const meetingRewardDeals = React.useMemo(() => {
    if (!isDirectPayout) return [];
    const now = new Date();
    return (deals.data ?? []).filter((d) => isMeetingRewardReminderDue(d, now));
  }, [deals.data, isDirectPayout]);
  const [claimingDealId, setClaimingDealId] = React.useState<string | null>(null);
  const claimingDeal = (deals.data ?? []).find((d) => d.id === claimingDealId) ?? null;
  // Dismiss = the one-time stop marker (meeting_reward_reminder_stopped_at).
  const dismissMeetingReward = useMutation({
    mutationFn: (dealId: string) => stopMeetingRewardReminder(accessToken!, dealId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals", "me"] });
    },
    onError: () => {
      toast(dealToasts.failed("dismiss the reward reminder"));
    },
  });

  // CHP-798: count of the partner's live deals expiring in the next 30 days,
  // for the "Deals expiring" scorecard. Uses the shared dealExpiry predicate
  // (window 30, NOT the 14-day expiring_soon) so the count matches the app's
  // single source of truth for "expiring within N days".
  const expiringCount = React.useMemo(
    () =>
      (deals.data ?? []).filter(
        (d) => d.deal_status === "live" && isExpiringWithin(d, Date.now(), 30),
      ).length,
    [deals.data],
  );

  return (
    <div className="container py-6">
      <DealDashboardHeader searchRef={searchRef} trainingBlocked={lockout.blocked} />

      <DealDashboardMetricsSection onCardClick={selectTab} expiringCount={expiringCount} />

      {/* FEED — soonest-expiring + resumable drafts; full-width to-do cards
          above the workspace. Renders a zero-state message when empty
          (conventions sweep review-r2) rather than collapsing. */}
      <div className="mb-6">
        <DealFeedSidebar
          expiring={expiring.data ?? []}
          drafts={activeDrafts}
          outcomes={outcomeDeals}
          expiredContext={expiredContextDeals}
          reviewingDeal={reviewingDeal}
          onReviewOptions={setReviewingDealId}
          onCloseReview={() => setReviewingDealId(null)}
          onResumeDraft={(draftId) =>
            void navigate({
              to: "/dashboard/partner/deals/new",
              search: { draftId },
            })
          }
          onDiscardDraft={(dealId) => discard.mutate(dealId)}
          isDiscarding={discard.isPending}
          onDismissExpiring={(dealId) => dismissExpiring.mutate(dealId)}
          onDismissDraft={(dealId) => dismissDraft.mutate(dealId)}
          onDismissOutcome={(dealId) => dismissOutcome.mutate(dealId)}
          onViewOutcome={openDeal}
          // CHP-464: direct-payout first-meeting-reward midpoint cards.
          meetingRewards={meetingRewardDeals}
          onClaimMeetingReward={setClaimingDealId}
          onDismissMeetingReward={(dealId) => dismissMeetingReward.mutate(dealId)}
          // CHP-893: drives the training-aware empty-feed copy. No onOpenTraining
          // is wired here, so this never surfaces a feed training card (that card
          // lives on the dashboard) — it only informs the zero-state wording.
          outstandingTraining={lockout.outstandingCount}
        />
      </div>

      {/* WORKSPACE — tabs + debounced search + enriched data grid. */}
      {/* scroll-mt clears the two stacked sticky headers (app bar h-12 +
          dashboard header) so a summary-card click scrolls the ledger's tab
          bar into view, not behind them (audit minor m5, completes CHP-473). */}
      <div ref={ledgerRef} className="scroll-mt-32">
        {deals.isLoading ? (
          <p role="status">Loading your deals...</p>
        ) : deals.error ? (
          <p role="alert">Couldn't load your deals.</p>
        ) : (
          <DealWorkspace
            deals={deals.data ?? []}
            metrics={dashboard.data}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchRef={searchRef}
            onDealClick={openDeal}
            filters={filters}
            onFiltersChange={setFilters}
            defaultSort={sortPreset ?? undefined}
            trainingBlocked={lockout.blocked}
            // CHP-891/892: the zero-state action is training-aware — blocked
            // partners are sent to training; everyone else to the wizard.
            emptyAction={
              lockout.blocked ? (
                <Button asChild size="sm">
                  <Link to="/dashboard/partner/training">
                    <GraduationCap className="h-4 w-4" aria-hidden /> Go to Training
                  </Link>
                </Button>
              ) : (
                <Button asChild size="sm">
                  <Link to="/dashboard/partner/deals/new">
                    <Plus className="h-4 w-4" aria-hidden /> Register Deal
                  </Link>
                </Button>
              )
            }
          />
        )}
      </div>

      <DealDetailDrawer dealId={selectedDealId} onClose={closeDrawer} />

      {/* CHP-464: the reward card's [Claim reward] opens the claim form. */}
      {claimingDeal ? (
        <MeetingRewardClaimModal
          deal={claimingDeal}
          open={claimingDealId !== null}
          onOpenChange={(open) => {
            if (!open) setClaimingDealId(null);
          }}
        />
      ) : null}
    </div>
  );
}
